/**
 * Picks a deterministic stock photo URL for an item that has no uploaded
 * image. Resolution order: name keyword → `kind` default → generic
 * fallback. The `kind` default (ADM-636) keeps placeholders on-theme —
 * a book lands on a book visual, an accommodation on a holiday one —
 * instead of the generic bungalow used for everything before.
 */

const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=800&q=80";

// Image par défaut contextualisée par `kind` de l'item (ADM-636).
// Sert de repli quand le nom de l'item ne matche aucun mot-clé : une
// collection de livres tombe sur un visuel de livre plutôt que sur le
// bungalow générique, un hébergement sur un visuel vacances, etc.
const KIND_IMAGES: Record<string, string> = {
  hebergement: "https://images.unsplash.com/photo-1668015642451-a3bb11afb441?w=800&q=80",
  materiel: "https://images.unsplash.com/photo-1671040690726-b78261eff126?w=800&q=80",
  vehicule: "https://images.unsplash.com/photo-1547744822-13c08fc36b3a?w=800&q=80",
  salle: "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=800&q=80",
  emplacement: "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=800&q=80",
  livre: "https://images.unsplash.com/photo-1610116306796-6fea9f4fae38?w=800&q=80",
};

const KEYWORD_IMAGES: Array<{ pattern: RegExp, url: string }> = [
  // Appartements de vacances
  {
    pattern: /apparte?ment/i,
    url: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&q=80",
  },
  // Stations de ski / montagne
  {
    pattern: /plagne|montagne|alpe|chalet|ski|station/i,
    url: "https://images.unsplash.com/photo-1518602164578-cd0074062767?w=800&q=80",
  },
  // Bord de mer
  {
    pattern: /agde|mer|plage|atlantique|méditerranée|cap[\s-]/i,
    url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80",
  },
  // Mobil-home / camping
  {
    pattern: /mobil[\s-]*home|camping|caravane/i,
    url: "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=800&q=80",
  },
  // Gîtes / campagne
  {
    pattern: /g[iî]te|maison|campagne|provence|lub[ée]ron/i,
    url: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800&q=80",
  },
  // Salle de fête / événement
  {
    pattern: /salle\s+(des\s+)?f[eê]te|événement|réception|mariage/i,
    url: "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=800&q=80",
  },
  // Salle de réunion
  {
    pattern: /salle.*r[ée]union|s[ée]minaire|coworking|bureau/i,
    url: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80",
  },
  // Outils / matériel de bricolage
  {
    pattern: /perceuse|outil|bricolage|mat[ée]riel|scie|ponceuse/i,
    url: "https://images.unsplash.com/photo-1581244277943-fe4a9c777189?w=800&q=80",
  },
  // Vélos
  {
    pattern: /v[ée]lo|bicyclette|trottinette/i,
    url: "https://images.unsplash.com/photo-1485965120184-e220f721d03e?w=800&q=80",
  },
  // Véhicules utilitaires
  {
    pattern: /camionnette|utilitaire|kangoo|berlingo|voiture|véhicule|vehicule/i,
    url: "https://images.unsplash.com/photo-1547744822-13c08fc36b3a?w=800&q=80",
  },
];

export const getItemImageUrl = (itemName: string, kind?: string | null): string => {
  // 1. Mot-clé du nom (le plus spécifique) — ex. « Vélo » → vélo.
  for (const { pattern, url } of KEYWORD_IMAGES) {
    if (pattern.test(itemName)) return url;
  }
  // 2. Repli contextualisé par kind (ex. livre → image de livre).
  if (kind && KIND_IMAGES[kind]) return KIND_IMAGES[kind];
  // 3. Repli générique.
  return DEFAULT_IMAGE;
};
